import dayjs from 'dayjs'
import { isEqual, pick, sortBy } from 'es-toolkit'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useNavigate, Link, useParams } from 'react-router-dom'
import { Button } from 'reactstrap'

import { COLOR_TYPES } from 'api/constants'
import { PLAN_UPDATE_MODE_TYPES } from 'api/plans/constants'
import type { UpdatePlanBulk, UpdatePlanSchedule } from 'api/plans/types'
import type { ColorType } from 'api/types'

import { getPlanByDate, getPlanList, updatePlanBulkCreate, selectPlansStatus } from 'slices/plansSlice'
import { getScheduleTypeList, selectScheduleTypesStatus } from 'slices/scheduleTypesSlice'
import { selectSessionStatus } from 'slices/sessionSlice'
import { getWorkspaceList, selectWorkspacesStatus } from 'slices/workspacesSlice'

import EditChangesDiscardDialog from 'components/EditChangesDiscardDialog/EditChangesDiscardDialog'
import SupportConfirm from 'components/SupportConfirm/SupportConfirm'
import {
  TimeScale,
  WorkerIcon,
  MultipleFooter,
  WorksSelector,
  WorkerPopover,
  ShiftBar,
  SubmitFooter,
} from 'components/common'
import { TENTATIVE_SCHEDULE_TYPE_ID, TIME_INTERVAL } from 'components/common/constants'
import type { MultipleFooterItem, EditSchedule, SelectedScheduleType } from 'components/common/types'
import { getShiftBarWidthByDuration, getUpdateWorkerSchedules } from 'components/common/utils'

import useBusinessTime from 'hooks/useBusinessTime'
import usePlans from 'hooks/usePlans'
import useWorkNotification from 'hooks/useWorkNotification'

import TeamWorkPlanCard from './TeamWorkPlanCard'
import { TimeRangeSelector } from './TimeRangeSelector'

import styles from './TeamWorkPlan.module.scss'

import type { ScheduleType } from './ShiftInputScale'
import type { TeamWorkPlanCardItem } from './TeamWorkPlanCard'

type CurrentSchedule = {
  workerId: number
  schedule: EditSchedule
  scheduleType: SelectedScheduleType
}

type EditWorkerSchedule = {
  workerId: number
  name: string
  schedules: EditSchedule[]
  selected: boolean
}

type InputScheduleType = {
  startHour: string
  startMinute: string
  endHour: string
  endMinute: string
}

const initInputSchedule = {
  startHour: '00',
  startMinute: '00',
  endHour: '00',
  endMinute: '00',
}

export const TeamWorkPlan = () => {
  const [viewState, setViewState] = useState(false)
  const [inputMode, setInputMode] = useState(false)
  const [selectorOpen, setSelectorOpen] = useState(false)
  const [timesSelectorOpen, setTimesSelectorOpen] = useState(false)
  const [editChangesDiscardDialogOpen, setEditChangesDiscardDialogOpen] = useState(false)
  const [currentSchedule, setCurrentSchedule] = useState<CurrentSchedule | undefined>()
  const [selectedScheduleType, setSelectedScheduleType] = useState<ScheduleType | undefined>()
  const [workers, setWorkers] = useState<EditWorkerSchedule[]>([])
  const [initWorkers, setInitWorkers] = useState<EditWorkerSchedule[]>([])
  const [inputSchedule, setInputSchedule] = useState<InputScheduleType>(initInputSchedule)
  const [openSupportConfirm, setOpenSupportConfirm] = useState(false)
  const [onApprove, setOnApprove] = useState<() => void>(() => {})

  const {
    team: { workspaceId, groupId, groupName, workerId },
  } = useSelector(selectSessionStatus, shallowEqual)
  const { partialScheduleTypes } = useSelector(selectScheduleTypesStatus, shallowEqual)
  const { partialWorkspaces } = useSelector(selectWorkspacesStatus, shallowEqual)
  const { plans, planList, isRequesting, errorMessage } = useSelector(selectPlansStatus, shallowEqual)

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const {
    planWorkDate,
    planScheduleTypes,
    planLastUpdatedAt,
    planLastUpdater,
    getEditSchedulesFromWorkPlan,
    getWorkerPlanFromUpdatePlanSchedule,
  } = usePlans()
  const { setSubmitted, submitted } = useWorkNotification()

  const { businessStartTime, businessEndTime, businessDuration, getShiftBarXbyStartTime, getTimeOver24h } =
    useBusinessTime({ interval: TIME_INTERVAL.FIVE })

  const params = useParams<'date'>()
  const date = useMemo(() => String(params.date), [params.date])

  useEffect(() => {
    dispatch(getWorkspaceList())
  }, [dispatch])

  useEffect(() => {
    dispatch(getScheduleTypeList(workspaceId))
  }, [dispatch, workspaceId])
  useEffect(() => {
    if (!date) {
      return
    }
    dispatch(getPlanList(workspaceId, date, date))
    dispatch(getPlanByDate(workspaceId, date))
  }, [dispatch, workspaceId, date])

  const isSupport = useMemo(() => selectedScheduleType?.color === 'secondary', [selectedScheduleType?.color])

  useEffect(() => {
    const workerGroup = plans?.groups.find(g => g.groupName === groupName)
    const newWorkers =
      workerGroup?.workersPlan
        .filter(w => w.workShifts.some(shift => shift))
        .map(w => {
          return {
            workerId: w.workerId,
            name: w.workerName,
            schedules: getEditSchedulesFromWorkPlan(w, workerGroup.isSupported),
            selected: false,
          }
        }) || []
    setInitWorkers(newWorkers)
    setWorkers(newWorkers)
  }, [getEditSchedulesFromWorkPlan, groupId, groupName, plans])

  const workPlanCardItems = useMemo(() => {
    if (!plans) {
      return []
    }

    const targetDailyPlan = planList?.dailyPlans.find(d => d.workDate === date)?.data
    if (!targetDailyPlan) {
      return []
    }

    return planScheduleTypes.reduce((acc: TeamWorkPlanCardItem[], cur) => {
      const target = targetDailyPlan.find(p => p.scheduleTypeId === cur.id)

      if (cur.dataConnection && target) {
        acc.push({
          label: cur.name,
          color: cur.color,
          targetValue: target.target,
          planValue: target.plan,
        })
      }
      return acc
    }, [])
  }, [plans, planList?.dailyPlans, planScheduleTypes, date])

  const title = useMemo(() => `${dayjs(date).locale('ja').format('YYYY/MM/DD（ddd）')}の作業計画`, [date])

  const inputStartAt = useMemo(() => {
    if (inputSchedule) {
      return dayjs(`${date} ${inputSchedule.startHour}:${inputSchedule.startMinute}`).utc().format()
    }
    return ''
  }, [date, inputSchedule])
  const inputDuration = useMemo(
    () =>
      inputSchedule
        ? dayjs(`${date} ${inputSchedule.endHour}:${inputSchedule.endMinute}`)
            .second(0)
            .diff(dayjs(`${date} ${inputSchedule.startHour}:${inputSchedule.startMinute}`).second(0), 'second')
        : 0,
    [date, inputSchedule]
  )
  const start = useMemo(() => {
    if (inputMode) {
      const startTime = dayjs(inputStartAt).local().format('HH:mm')
      return getTimeOver24h(startTime, true)
    }
    return dayjs(currentSchedule?.schedule.startAt).format('HH:mm')
  }, [inputStartAt, currentSchedule, inputMode, getTimeOver24h])
  const end = useMemo(() => {
    if (inputMode) {
      const endTime = dayjs(inputStartAt).local().add(inputDuration, 'seconds').format('HH:mm')
      return getTimeOver24h(endTime)
    }
    return dayjs(currentSchedule?.schedule.startAt)
      .add(currentSchedule?.schedule.duration || 0, 'seconds')
      .format('HH:mm')
  }, [inputDuration, inputStartAt, currentSchedule, inputMode, getTimeOver24h])

  const updateWorkerSchedules = useCallback(
    (
      scheduleTypeId: number,
      supportWorkspaceId: number | null,
      supportWorkspaceName: string | null,
      schedules: EditSchedule[]
    ) => {
      if (inputDuration <= 0) {
        return schedules
      }

      return getUpdateWorkerSchedules(
        inputStartAt,
        inputDuration,
        scheduleTypeId,
        supportWorkspaceId,
        supportWorkspaceName,
        schedules
      )
    },
    [inputStartAt, inputDuration]
  )

  const addSchedule = useCallback(() => {
    if (!selectedScheduleType) {
      return
    }

    const scheduleTypeId = isSupport ? TENTATIVE_SCHEDULE_TYPE_ID.SUPPORT : selectedScheduleType.scheduleTypeId
    const supportWorkspaceId = isSupport ? selectedScheduleType.scheduleTypeId : null
    const supportWorkspaceName = isSupport ? selectedScheduleType.name : null

    const newWorkers = workers.map(w => {
      if (w.selected) {
        return {
          ...w,
          selected: false,
          schedules: updateWorkerSchedules(scheduleTypeId, supportWorkspaceId, supportWorkspaceName, w.schedules),
        }
      }
      return w
    })

    setWorkers(newWorkers)
    setInputMode(false)
    setSelectedScheduleType(undefined)
    setInputSchedule(initInputSchedule)
    setTimesSelectorOpen(false)
  }, [isSupport, selectedScheduleType, workers, updateWorkerSchedules])

  const deleteWorkerSchedule = useCallback(
    (targetWorkerId: number, scheduleId: number | null) => {
      const newWorkers = workers.map(w => ({
        ...w,
        schedules: w.schedules.filter(s => w.workerId !== targetWorkerId || s.scheduleId !== scheduleId),
      }))
      setWorkers(newWorkers)
    },
    [workers]
  )

  const handleOpenSupportConfirm = (func: () => void, support: boolean) => {
    setOnApprove(() => func)
    if (support) {
      setOpenSupportConfirm(true)
    } else {
      func()
    }
  }

  // 開始時間が終了時間前であるかと営業時間内かチェック
  const isValidScheduleTime = useMemo(() => {
    if (!inputSchedule) {
      return false
    }

    const [businessStartHour, businessStartMinute] = businessStartTime.split(':').map(Number)
    const [businessEndHour, businessEndMinute] = businessEndTime.split(':').map(Number)

    if (
      (Number(inputSchedule.startHour) === businessStartHour &&
        Number(inputSchedule.startMinute) < businessStartMinute) ||
      (Number(inputSchedule.endHour) === businessEndHour && Number(inputSchedule.endMinute) > businessEndMinute)
    ) {
      return false
    }

    return (
      Number(inputSchedule.startHour) < Number(inputSchedule.endHour) ||
      (Number(inputSchedule.startHour) === Number(inputSchedule.endHour) &&
        Number(inputSchedule.startMinute) < Number(inputSchedule.endMinute))
    )
  }, [businessEndTime, businessStartTime, inputSchedule])

  const footer = useMemo((): MultipleFooterItem => {
    const disabled = workers.every(w => !w.selected) || !!selectedScheduleType
    if (selectorOpen) {
      return {
        disabled,
        stepNumber: 2,
        stepText: '作業内容を選択',
        decisionButtonClick: () => null,
      }
    }
    if (!selectorOpen && !!selectedScheduleType) {
      setTimesSelectorOpen(true)
      return {
        disabled: !inputSchedule || !isValidScheduleTime,
        stepNumber: 3,
        stepText: '時間帯を選択',
        decisionButtonClick: () =>
          handleOpenSupportConfirm(() => {
            addSchedule()
          }, isSupport),
      }
    }
    return {
      disabled,
      stepNumber: 1,
      stepText: 'スケジュールを変更したいユーザーを選択',
      decisionButtonClick: () => setSelectorOpen(true),
    }
  }, [workers, selectedScheduleType, selectorOpen, inputSchedule, isValidScheduleTime, isSupport, addSchedule])

  const workerActiveRange = (schedules: EditSchedule[]) => {
    const sortedShifts = sortBy(schedules, ['startAt']).filter(
      s => s.scheduleTypeId === TENTATIVE_SCHEDULE_TYPE_ID.SHIFT
    )
    if (sortedShifts.length === 0) {
      return
    }
    const range: Array<[number, number]> = sortedShifts.map(s => {
      const startX = getShiftBarXbyStartTime(s.startAt, planWorkDate)
      const endX = getShiftBarWidthByDuration(s.duration, TIME_INTERVAL.FIVE) + startX - 1
      return [startX, endX]
    })
    return range
  }

  const WorkerPopoverClickHandler = (
    targetWorkerId: number,
    schedule: EditSchedule,
    scheduleType: SelectedScheduleType
  ) => {
    setCurrentSchedule({ workerId: targetWorkerId, schedule, scheduleType })
    setSelectorOpen(true)
  }

  const getShiftBarItems = (schedules: EditSchedule[], targetWorkerId: number) => {
    return schedules
      .filter(d => d.scheduleTypeId !== TENTATIVE_SCHEDULE_TYPE_ID.SHIFT)
      .map((d, index) => {
        const x = getShiftBarXbyStartTime(d.startAt, planWorkDate)
        const width = getShiftBarWidthByDuration(d.duration, TIME_INTERVAL.FIVE)

        const support = d.scheduleTypeId === TENTATIVE_SCHEDULE_TYPE_ID.SUPPORT
        const scheduleType = support
          ? { id: d.supportWorkspaceId || 0, name: d.supportWorkspaceName || '', color: undefined }
          : pick(partialScheduleTypes.find(s => s.id === d.scheduleTypeId)!, ['id', 'name', 'color'])

        return {
          id: `item-${groupId}-${index}`,
          content: (
            <WorkerPopover
              startAt={d.startAt}
              duration={d.duration}
              scheduleType={scheduleType}
              disabled={inputMode}
              onClick={() => WorkerPopoverClickHandler(targetWorkerId, d, scheduleType)}
              onDelete={() => deleteWorkerSchedule(targetWorkerId, d.scheduleId)}
            >
              <div className="mh-100">{scheduleType.name}</div>
            </WorkerPopover>
          ),
          x,
          width,
          color: scheduleType?.color,
        }
      })
  }

  const selectWorker = (targetWorkerId: number) => {
    if (!inputMode || footer.stepNumber !== 1) {
      return
    }
    const newWorkers = workers.map(w => (w.workerId === targetWorkerId ? { ...w, selected: !w.selected } : w))
    setWorkers(newWorkers)
  }

  const moveToWorkerDetail = (targetWorkerId: number) => {
    if (inputMode) {
      return
    }

    navigate(`/team-worker/${targetWorkerId}`)
  }

  const cancelInputMode = () => {
    setSelectorOpen(false)
    setTimesSelectorOpen(false)
    setInputMode(false)
    setCurrentSchedule(undefined)
    setSelectedScheduleType(undefined)
    setInputSchedule(initInputSchedule)
    setWorkers(workers.map(d => ({ ...d, selected: false })))
  }

  const selectorWorkClick = (id: number, name: string, color?: ColorType) => {
    const workspace = !color ? partialWorkspaces.find(w => w.id === id) : undefined
    const scheduleTypeId = !color ? TENTATIVE_SCHEDULE_TYPE_ID.SUPPORT : id
    const supportWorkspaceId = !color ? id : null
    const supportWorkspaceName = workspace?.name || null

    if (inputMode) {
      if (!color) {
        setSelectedScheduleType(
          workspace ? { scheduleTypeId: workspace.id, name: workspace.name, color: 'secondary' } : workspace
        )
      } else {
        const targetScheduleType = partialScheduleTypes.find(s => s.id === id)
        setSelectedScheduleType(
          targetScheduleType && {
            scheduleTypeId: targetScheduleType.id,
            name: targetScheduleType.name,
            color: targetScheduleType.color,
          }
        )
      }
    } else {
      const newWorkers = workers.map(cur => {
        if (cur.workerId === currentSchedule?.workerId) {
          return {
            ...cur,
            schedules: cur.schedules.map(s =>
              isEqual(s, currentSchedule.schedule)
                ? { ...s, scheduleTypeId, supportWorkspaceId, supportWorkspaceName }
                : s
            ),
          }
        }
        return cur
      })
      if (!color) {
        setSelectedScheduleType(
          workspace ? { scheduleTypeId: workspace.id, name: workspace.name, color: 'secondary' } : workspace
        )
      }
      handleOpenSupportConfirm(() => {
        setWorkers(newWorkers)
        setCurrentSchedule(undefined)
        setSelectedScheduleType(undefined)
      }, !color)
    }
    setSelectorOpen(false)
  }

  const unchanged = useMemo(() => isEqual(workers, initWorkers), [initWorkers, workers])
  const onCancel = () => {
    setWorkers(initWorkers)
    setEditChangesDiscardDialogOpen(false)
  }

  const createUpdateSchedulesData = useCallback(() => {
    const schedules = workers.flatMap(cur => {
      const initWorkersSchedules = initWorkers.find(init => init.workerId === cur.workerId)?.schedules || []

      const newSchedules: UpdatePlanSchedule[] = cur.schedules.map(s => ({
        scheduleId: null,
        schedule: {
          scheduleTypeId: s.scheduleTypeId,
          supportWorkspaceId: s.supportWorkspaceId,
          startAt: s.startAt,
          duration: s.duration,
          workerId: cur.workerId,
          groupId: null,
        },
      }))

      const deleteSchedule: UpdatePlanSchedule[] = initWorkersSchedules
        .filter(init => !cur.schedules.some(edit => edit.scheduleId === init.scheduleId))
        .map(init => ({ scheduleId: init.scheduleId, schedule: null }))

      const updateSchedules: UpdatePlanSchedule[] = cur.schedules.map(s => ({
        scheduleId: s.scheduleId,
        schedule: {
          scheduleTypeId: s.scheduleTypeId,
          supportWorkspaceId: s.supportWorkspaceId,
          startAt: s.startAt,
          duration: s.duration,
          workerId: cur.workerId,
          groupId: null,
        },
      }))

      return newSchedules.concat(deleteSchedule, updateSchedules)
    }, [])

    return getWorkerPlanFromUpdatePlanSchedule(schedules)
  }, [getWorkerPlanFromUpdatePlanSchedule, initWorkers, workers])

  useEffect(() => {
    if (!submitted || isRequesting || !date) {
      return
    }

    if (errorMessage === '') {
      dispatch(getPlanByDate(workspaceId, date))
    }
  }, [submitted, isRequesting, errorMessage, dispatch, workspaceId, date])

  const onSubmit = () => {
    if (!plans || !date) {
      return
    }
    setSubmitted(true)

    const updatePlanDate: UpdatePlanBulk = {
      updateMode: PLAN_UPDATE_MODE_TYPES.WORK_PLAN,
      workersPlan: createUpdateSchedulesData(),
      updatedByWorkerId: workerId,
    }
    dispatch(updatePlanBulkCreate(workspaceId, date, updatePlanDate))
  }

  const currentGroupColor = plans?.groups.find(g => g.groupName === groupName)?.groupColor || COLOR_TYPES.SILVER

  const handleTimeChange = useCallback(
    (changeStartHour: string, changeStartMinute: string, changeEndHour: string, changeEndMinute: string) => {
      setInputSchedule({
        startHour: changeStartHour,
        startMinute: changeStartMinute,
        endHour: changeEndHour,
        endMinute: changeEndMinute,
      })
    },
    []
  )

  return (
    <>
      <div className={styles.container}>
        <Link
          to={`/team-schedules`}
          className="d-flex align-items-center text-secondary w-25 ps-4 pt-4 text-decoration-none"
        >
          <i className="icf-chevron_left" />
          <span style={{ marginTop: '0.2rem' }}>日付一覧へ</span>
        </Link>
        <div className="d-flex align-items-center px-4">
          <div className="font-x-large fw-bold m-0 flex-grow-1">{title}</div>
          <div className="bg-white rounded">
            <Button outline className="d-flex align-items-center" onClick={() => setViewState(!viewState)}>
              <i className={`icf-carot_${viewState ? `down` : `right`} pe-2 font-large`} />
              <div className="ps-1">目標と計画の比較</div>
            </Button>
          </div>
          <div className="bg-white rounded ms-2">
            <Button
              outline
              className="d-flex align-items-center"
              disabled={inputMode}
              onClick={() => setInputMode(true)}
            >
              <i className="icf-edit pe-2 font-large" />
              <div className="ps-1">作業計画の入力</div>
            </Button>
          </div>
        </div>
        <div className="my-3 px-4 d-flex text-nowrap overflow-auto">
          {viewState &&
            workPlanCardItems.map(item => (
              <TeamWorkPlanCard
                key={item.label}
                label={item.label}
                color={item.color}
                targetValue={item.targetValue}
                planValue={item.planValue}
              />
            ))}
        </div>

        <div className={styles.tableWrapper}>
          <table>
            <thead>
              <tr className="border-bottom">
                <td className={`bg-secondary-pale px-3 ${styles.tableHeader}`}>名前</td>
                <td className="p-0">
                  <TimeScale is5min />
                </td>
              </tr>
            </thead>
            <tbody>
              {workers.map(worker => (
                <tr key={worker.workerId} className={styles.tableRow}>
                  <td
                    className={`px-3 ${worker.selected && 'bg-primary text-white'} ${styles.tableHeader}`}
                    onClick={() => selectWorker(worker.workerId)}
                  >
                    <div className="d-flex align-items-center">
                      <WorkerIcon name={worker.name} groupColor={currentGroupColor} />
                      <span className="ps-3 pe-1 flex-grow-1 text-truncate">{worker.name}</span>
                      <i
                        className={`icf-info font-large text-${worker.selected ? 'white' : 'secondary'}`}
                        onClick={() => moveToWorkerDetail(worker.workerId)}
                      />
                    </div>
                  </td>
                  <td className={styles.tableContent}>
                    <ShiftBar
                      items={getShiftBarItems(worker.schedules, worker.workerId)}
                      businessStartTime={businessStartTime}
                      shiftBarWidth={businessDuration}
                      activeRange={workerActiveRange(worker.schedules)}
                      disabled={inputMode}
                      isTeam={true}
                      is5min
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <WorksSelector
        open={selectorOpen}
        scheduleTypes={partialScheduleTypes}
        workspaces={partialWorkspaces}
        currentScheduleType={currentSchedule?.scheduleType}
        workspaceId={workspaceId}
        onClose={cancelInputMode}
        onWorkClick={selectorWorkClick}
      />
      <TimeRangeSelector
        open={timesSelectorOpen}
        startHour={inputSchedule.startHour}
        startMinute={inputSchedule.startMinute}
        endHour={inputSchedule.endHour}
        endMinute={inputSchedule.endMinute}
        onClose={cancelInputMode}
        onTimeChange={handleTimeChange}
        key={`TimeRangeSelector-${timesSelectorOpen}`}
      />
      {inputMode && (
        <MultipleFooter
          stepNumber={footer.stepNumber}
          stepText={footer.stepText}
          disabled={footer.disabled}
          selectorOpen={selectorOpen}
          decisionButtonClick={footer.decisionButtonClick}
          onCancel={cancelInputMode}
        />
      )}
      {!inputMode && !unchanged && (
        <SubmitFooter
          onCancel={() => setEditChangesDiscardDialogOpen(true)}
          onSubmit={onSubmit}
          updatedBy={planLastUpdater}
          updatedAt={planLastUpdatedAt}
        />
      )}

      <EditChangesDiscardDialog
        isOpen={editChangesDiscardDialogOpen}
        onCancel={() => setEditChangesDiscardDialogOpen(false)}
        onDiscard={onCancel}
      />

      <SupportConfirm
        isOpen={openSupportConfirm}
        start={start}
        end={end}
        name={selectedScheduleType?.name || ''}
        onCancel={() => setOpenSupportConfirm(false)}
        onApprove={() => {
          onApprove?.()
          setOpenSupportConfirm(false)
        }}
      />
    </>
  )
}
